// NGRP Maharashtra Launch Blueprint — 10-phase bottom-up rollout plan.
// Content authored by the party leadership. Rendered on /roadmap.

export const ROADMAP = {
  title: "NGRP Maharashtra Launch Blueprint",
  subtitle: "A bottom-up political institution. Not a membership drive.",
  intro:
    "NGRP is being built as a bottom-up political institution, not just a membership drive. The blueprint below shows how the party will be founded, grown, trained and structured \u2014 with transparency, merit, decentralisation and accountability designed in from day one.",

  positioning: {
    mission_marathi: "\u0930\u093e\u091c\u0915\u093e\u0930\u0923\u093e\u092a\u0947\u0915\u094d\u0937\u093e \u0938\u0941\u0936\u093e\u0938\u0928 \u2014 \u0938\u0924\u094d\u0924\u0947\u092a\u0947\u0915\u094d\u0937\u093e \u0935\u094d\u092f\u0935\u0938\u094d\u0925\u093e \u092a\u0930\u093f\u0935\u0930\u094d\u0924\u0928.",
    mission_english: "Governance before politics. Systems before personalities.",
    pillars: [
      "Zero-corruption government systems",
      "Time-bound citizen services",
      "Merit and competence in political leadership",
      "Employment, entrepreneurship and productive economic growth",
      "Transparent public expenditure and accountability",
    ],
    note: "Don\u2019t try to publish 100 promises at launch. People should remember five.",
  },

  phases: [
    {
      number: 1,
      window: "Days 1\u201330",
      title: "Founding Core \u2014 First 100 people",
      body:
        "Create a 100-member Founding Council before any large public launch. Target a balanced mix: UPSC/MPSC aspirants (past and present), engineers, doctors, lawyers/CAs, teachers/professors, entrepreneurs, farmers, women leaders, students/youth, retired government officers and credible grassroots/social workers.",
      selection_question:
        "The first question is not \u201chow many votes can you bring?\u201d The question is: \u201cWhat problem in Maharashtra are you capable of helping solve?\u201d",
      criteria: [
        "Integrity",
        "Competence",
        "Willingness to work",
        "Public credibility",
        "Commitment to constitutional democracy",
      ],
      teams: [
        "Organisation",
        "Policy / Research",
        "Legal & Compliance",
        "Communications",
        "Technology / Data",
        "Membership",
        "Training",
        "Finance",
        "Grassroots Public Issues",
      ],
    },
    {
      number: 2,
      title: "Create the Political Proposition",
      body:
        "NGRP needs a message people can understand within 20 seconds. The party is built around approximately five public pillars \u2014 not one hundred promises.",
      quote: "Governance before politics. Systems before personalities.",
    },
    {
      number: 3,
      title: "Build the Organisational Pyramid",
      body:
        "The organisation grows upward, not downward: Booth/Ward \u2192 Village \u2192 Cluster \u2192 Taluka \u2192 District \u2192 Assembly \u2192 Lok Sabha \u2192 State \u2192 National. Each level has real responsibilities, not decorative posts.",
      pyramid: [
        "Booth / Ward",
        "Village",
        "Cluster",
        "Taluka",
        "District",
        "Assembly Constituency",
        "Lok Sabha Segment",
        "Maharashtra State",
        "National",
      ],
      levels_detail: [
        {
          level: "Village",
          body: "NGRP Gram Governance Team of 7\u201315 active people. Identify village problems, maintain a public-issues register, help citizens navigate services, study Gram Panchayat expenditure, hold monthly public meetings and escalate unresolved matters.",
        },
        {
          level: "Taluka",
          body: "Each Taluka Committee coordinates village teams and maintains a Taluka Governance Dashboard.",
        },
        {
          level: "District",
          body: "The District Council is the operational centre for membership, training, media, policy feedback, legal support and constituency preparation.",
        },
        {
          level: "Assembly Constituency",
          body: "Constituency Governance Team + Policy Team + Public Grievance Cell + Youth Network + Women\u2019s Network + Digital Team \u2014 the infrastructure from which future candidates emerge.",
        },
      ],
    },
    {
      number: 4,
      title: "Public Leadership Academy \u2014 The Biggest Talent Pool",
      body:
        "The NGRP Public Leadership Academy is a distinctive party institution. It invites interested UPSC/MPSC aspirants and other capable citizens to study, then work in the field.",
      curriculum: [
        "Constitution",
        "Economics",
        "Public Administration",
        "Local Government",
        "Government Budgeting",
        "Agriculture",
        "Infrastructure",
        "Technology",
        "Ethics",
        "Legislative Process",
        "Public Communication",
      ],
      field_rule:
        "Then fieldwork. An aspirant does not become a leader merely because they studied polity. Give them a village or ward and ask: \u201cIdentify 20 important citizen problems and demonstrate that you can work constructively toward solving them.\u201d Knowledge + field performance determines advancement.",
    },
    {
      number: 5,
      window: "Days 30\u201360",
      title: "First 1,000 Members via 100 \u00d7 10",
      body:
        "Instead of random online recruitment, each of the 100 founders identifies approximately 10 high-quality potential members. That builds a first network of around 1,000 people.",
      program: "Governance Dialogues (not conventional political rallies).",
      example_question:
        "\u201cIf Maharashtra could reform five government systems in the next five years, what should they be?\u201d Collect answers digitally \u2014 simultaneously recruiting and building the manifesto from public input.",
    },
    {
      number: 6,
      window: "Days 60\u2013100",
      title: "1,000 \u2192 10,000 Governance Reformers",
      body:
        "Launch a structured \u201c10,000 Governance Reformers\u201d campaign. Membership is not a numbers competition. People are separated into three levels.",
      tiers: [
        { name: "Supporter", body: "Endorses the mission." },
        { name: "Active Member", body: "Participates in local work." },
        {
          name: "Leadership Cadre",
          body: "Completes training, demonstrates field performance and accepts stronger ethical and accountability requirements.",
        },
      ],
    },
    {
      number: 7,
      title: "The NGRP Digital Platform \u2014 the Party's Operating System",
      body:
        "The website is an operating system for the party \u2014 not merely a political website. Citizens can:",
      capabilities: [
        "Join NGRP",
        "Volunteer",
        "Submit a local problem",
        "Suggest a policy",
        "Track an issue",
        "Attend an event",
        "Apply for leadership training",
        "View party finances & disclosures",
        "View local team performance",
      ],
      map:
        "A Maharashtra map that navigates District \u2192 Taluka \u2192 Village/Ward, showing local teams and public issues. Transparency itself is part of the product.",
    },
    {
      number: 8,
      title: "Don't Distribute Political Posts Casually",
      body:
        "No inflation of \u201cPresident / Vice President / General Secretary\u201d titles to attract members. Every position is responsibility-based, with measurable targets and a 90-day performance review.",
      examples: [
        { role: "Nashik District Coordinator \u2014 Membership", target: "Measurable membership growth targets." },
        { role: "Policy Coordinator \u2014 Agriculture", target: "Produces actual policy work." },
        { role: "Taluka Governance Coordinator", target: "Maintains functioning village teams." },
      ],
    },
    {
      number: 9,
      title: "Transparent Candidate Selection",
      body:
        "One of NGRP's strongest differentiators is a transparent candidate pipeline. Educational achievement helps, but is never the sole criterion.",
      pipeline: [
        "Application",
        "Background & eligibility screening",
        "Public-service record",
        "Policy / constitutional assessment",
        "Constituency work",
        "Public interaction",
        "Internal evaluation",
        "Candidate selection",
      ],
      principle: "Merit doesn\u2019t mean degree. Merit means competence + integrity + performance + public trust.",
    },
    {
      number: 10,
      window: "First 100 days",
      title: "100 Days \u2014 100 Governance Dialogues",
      body:
        "Instead of heavy spending on huge rallies, conduct meetings across Maharashtra with students, farmers, professionals, entrepreneurs, women, workers and civil-service aspirants. Every dialogue produces data.",
      deliverable: "Publish the Maharashtra People\u2019s Governance Report 2026.",
      report_stats: [
        { label: "Citizens consulted", value: "XX,XXX" },
        { label: "Villages / Wards represented", value: "X,XXX" },
        { label: "Problems documented", value: "XX,XXX" },
        { label: "Top governance reforms identified", value: "25" },
      ],
      closing: "This gives NGRP something more valuable than a launch speech \u2014 evidence of listening.",
    },
  ],

  growth_engine: [
    "100 Founders",
    "1,000 Active Members",
    "10,000 Governance Reformers",
    "Village / Ward Teams",
    "Taluka Organisations",
    "District Organisations",
    "Assembly Constituency Teams",
    "Lok Sabha Teams",
    "Maharashtra Organisation",
    "National Expansion (after Maharashtra model proven)",
  ],

  closing_principle:
    "The most important distinction: don\u2019t build NGRP around any single individual. Build a system capable of producing thousands of credible leaders. If the party promises institutional reform, its own internal organisation must demonstrate transparency, merit, decentralisation and accountability from day one.",
};
